import { useState } from 'react';

import { useKnownAvailableParams, useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { ENDORSEMENTS_HASH } from '#src/app/shared/constants';

import type { Endorsement } from '../../../api';
import { useDeleteEndorsement, useEndorsements } from '../../../hooks';

const ENDORSEMENTS_MAX_PER_PETITION = 10;

type UseEndorsementSectionWithListReturn = {
	endorsements: readonly Endorsement[];
	isAtEndorsementLimit: boolean;
	isContentExpanded: boolean;
	isDeleteModalOpen: boolean;
	isDeleting: boolean;
	setIsDeleteModalOpen: (open: boolean) => void;
	handleAddAnother: () => void;
	handleViewOnPetition: () => void;
	handleEdit: (id: string) => void;
	handleRemove: (id: string) => void;
	handleToggleContentExpansion: () => void;
	handleConfirmDelete: () => Promise<void>;
};

export function useEndorsementSectionWithList(): UseEndorsementSectionWithListReturn {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const navigate = useNavigate();
	const track = useTracking();
	const [isContentExpanded, setIsContentExpanded] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [endorsementToDelete, setEndorsementToDelete] = useState<string | null>(null);

	const {
		data: { petitionId, endorsements, totalCount },
		actions: { refetch },
	} = useEndorsements({ petitionSlugOrId: slug });

	const isAtEndorsementLimit = totalCount >= ENDORSEMENTS_MAX_PER_PETITION;

	const {
		data: { isDeleting },
		actions: { deleteEndorsement: deleteEndorsementAction },
	} = useDeleteEndorsement({
		petitionId,
		onSuccess: async () => {
			setIsDeleteModalOpen(false);
			setEndorsementToDelete(null);
			await refetch();
		},
	});

	const handleAddAnother = () => {
		void track('add_petition_endorsement');
		navigate(`/p/${slug}/dashboard/endorsements/add`);
	};

	const handleViewOnPetition = () => {
		void track('view_petition_from_endorsements');
		navigate(`/p/${slug}${ENDORSEMENTS_HASH}`, { preventScrollReset: true });
	};

	const handleEdit = (endorsementId: string) => {
		void track('edit_petition_endorsement');
		navigate(`/p/${slug}/dashboard/endorsements/edit/${endorsementId}`);
	};

	const handleRemove = (endorsementId: string) => {
		void track('delete_petition_endorsement');
		setEndorsementToDelete(endorsementId);
		setIsDeleteModalOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!endorsementToDelete) return;
		await deleteEndorsementAction(endorsementToDelete);
	};

	const handleToggleContentExpansion = () => {
		setIsContentExpanded((prev) => !prev);
	};

	return {
		endorsements,
		isAtEndorsementLimit,
		isContentExpanded,
		isDeleteModalOpen,
		isDeleting,
		setIsDeleteModalOpen,
		handleAddAnother,
		handleViewOnPetition,
		handleEdit,
		handleRemove,
		handleToggleContentExpansion,
		handleConfirmDelete,
	};
}
